import React, { ReactElement, FC, useRef, useState, useEffect } from 'react'
import { pathOr } from 'ramda'
import { FastField } from 'formik'
import { CleaveField, FormField, Heading, Radio, RadioGroup, TextField, TextInput } from '../..'
import { PartySearch } from '../../../containers'
import { formUtils } from '../../../../utils'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { $TSFixMe } from 'types/ts-migrate'
import { format, isValid } from 'date-fns'
import { transformTwoDigitYearToFour } from 'utils/date'
import PhoneField from 'views/components/atoms/PhoneField/PhoneField'
import { extractDialCode } from 'utils/formUtils'
import { createPhoneFieldChangeHandler, hasNumberBeyondDialCode } from './utils'

type TProps = {
  values?: any
  requiredFields?: string[]
  tags?: any
  handleChange?: (e: any) => void
  handleBlur?: (e: any) => void
  handleSelect?: (e: any) => void
  setFieldValue?: any
  errors?: any
  touched?: any
  submitCount?: number
  allowPartySearch?: boolean
  disableFields?: any
  personsOnly?: boolean
  handleStateIdentificationType: (e: any) => void
  stateIdentificationType?: string
}

const InviteUserDetails: FC<TProps> = ({
  values,
  requiredFields = [],
  tags,
  handleChange,
  handleBlur,
  handleSelect,
  setFieldValue,
  errors,
  touched,
  allowPartySearch = true,
  disableFields = false,
  personsOnly = false,
  handleStateIdentificationType,
  stateIdentificationType,
}): ReactElement => {
  const getFieldError = formUtils.getFieldError(0, touched, errors)
  const getFieldValue = (name: any) => pathOr('', [name], values)
  const isRequired = (name: string) => requiredFields.includes(name)

  let firstNameRef = useRef(null)
  const yy = getFieldValue('idNumber').substring(0, 2)
  const mm = getFieldValue('idNumber').substring(2, 4)
  const dd = getFieldValue('idNumber').substring(4, 6)

  const fourDigitYear = transformTwoDigitYearToFour(yy)
  const dob = new Date(parseInt(fourDigitYear ?? ''), parseInt(mm) - 1, parseInt(dd))
  const dateOfBirthFormatted = isValid(dob) ? format(dob, 'yyyy-MM-dd') : ''

  const cellNumber = getFieldValue('cellNumber')
  const cellNumberReplaced = cellNumber.replace(/\s/g, '')
  const telNumber = getFieldValue('telNumber')

  const [cellDialCode, setCellDialCode] = useState(() => extractDialCode(cellNumber))
  const [telDialCode, setTelDialCode] = useState(() => extractDialCode(telNumber))

  // Update dial codes when phone numbers change (e.g., when loading a contact)
  useEffect(() => {
    const newCellDialCode = extractDialCode(cellNumber)
    if (cellNumber && newCellDialCode !== cellDialCode) {
      setCellDialCode(newCellDialCode)
    }
  }, [cellNumber])

  useEffect(() => {
    const newTelDialCode = extractDialCode(telNumber)
    if (telNumber && newTelDialCode !== telDialCode) {
      setTelDialCode(newTelDialCode)
    }
  }, [telNumber])

  useEffect(() => {
    if (telNumber) {
      const dialCodeFromValue = extractDialCode(telNumber)
      if (!hasNumberBeyondDialCode(telNumber, dialCodeFromValue)) {
        setFieldValue('telNumber', '')
      }
    }
  }, [telNumber])

  return (
    <>
      <Heading as="h3" size="a5" spacing="small">
        Personal details
      </Heading>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <FastField
              name="firstName"
              onChange={handleChange}
              onBlur={handleBlur}
              render={({ field, form }: any) =>
                allowPartySearch ? (
                  <PartySearch
                    {...field}
                    includeIdentification
                    label="First name"
                    required={isRequired('firstName')}
                    disabled={disableFields}
                    value={getFieldValue('firstName')}
                    onResultSelect={handleSelect}
                    tags={tags}
                    error={getFieldError('firstName')}
                    limitResults={personsOnly ? 'person' : null}
                  />
                ) : (
                  <TextField
                    inputComponent={
                      <TextInput
                        type={TextFieldTypes.text}
                        name="firstName"
                        value={getFieldValue('firstName')}
                        disabled={disableFields}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        ref={(ref: any) => {
                          firstNameRef = ref
                        }}
                      />
                    }
                    label="First name"
                    required={isRequired('firstName')}
                    error={getFieldError('firstName')}
                  />
                )
              }
            />
          </FormField>
        </div>
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.text}
                  name="lastName"
                  value={getFieldValue('lastName')}
                  disabled={disableFields}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label="Last name"
              required={isRequired('lastName')}
              error={getFieldError('lastName')}
            />
          </FormField>
        </div>
      </div>
      <div className="rbn--row">
        <div className="rbn--col-md-6">
          <RadioGroup
            id="identificationType"
            name="identificationType"
            className="radio-group"
            stackRadios={false}
            label="Identification type"
            defaultSelected={stateIdentificationType}
            onChange={(e: $TSFixMe) => {
              handleStateIdentificationType(e.target.value)
              if (e.target.value === 'passportNumber') {
                setFieldValue('idNumber', '')
                setFieldValue('dateOfBirth', '')
              } else {
                setFieldValue('passport', '')
                setFieldValue('dateOfBirth', '')
              }
            }}
            disabled={disableFields}
          >
            <Radio
              id="saIdNumber"
              name="saIdNumber"
              value="saIdNumber"
              label="South African ID number"
              allowUncheck={false}
            />
            <Radio
              id="passportNumber"
              name="passportNumber"
              value="passportNumber"
              label="Passport number"
              allowUncheck={false}
            />
          </RadioGroup>
        </div>
      </div>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                stateIdentificationType === 'saIdNumber' ? (
                  <TextInput
                    type={TextFieldTypes.text}
                    name="idNumber"
                    value={getFieldValue('idNumber')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                ) : (
                  <TextInput
                    type={TextFieldTypes.text}
                    name="passport"
                    value={getFieldValue('passport')}
                    disabled={disableFields}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                )
              }
              label={stateIdentificationType === 'saIdNumber' ? 'ID number' : 'Passport number'}
              required={isRequired(stateIdentificationType === 'saIdNumber' ? 'idNumber' : 'passport')}
              error={getFieldError(stateIdentificationType === 'saIdNumber' ? 'idNumber' : 'passport')}
            />
          </FormField>
        </div>
        <div className="rbn--col-md-4">
          <FormField>
            <CleaveField
              name="dateOfBirth"
              type="date"
              id="dateOfBirth"
              label="Date of birth"
              labelHint={
                stateIdentificationType === 'saIdNumber'
                  ? 'Date of birth is automatically derived from the South African ID number'
                  : undefined
              }
              placeholder="YYYY-MM-DD"
              value={
                stateIdentificationType === 'saIdNumber' && getFieldValue('idNumber').length > 6
                  ? dateOfBirthFormatted
                  : values.dateOfBirth
              }
              onChange={handleChange || (() => {})}
              error={getFieldError('dateOfBirth')}
              disabled={stateIdentificationType === 'saIdNumber' || disableFields}
            />
          </FormField>
        </div>
      </div>
      <Heading as="h3" size="a5" spacing="small">
        Contact details
      </Heading>
      <div className="rbn--row">
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <TextInput
                  type={TextFieldTypes.email}
                  name="emailAddress"
                  value={getFieldValue('emailAddress')}
                  disabled={disableFields}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              }
              label="Email address"
              required={isRequired('emailAddress')}
              error={getFieldError('emailAddress')}
            />
          </FormField>
        </div>
        <div className="rbn--col-md-4">
          <FormField>
            <TextField
              inputComponent={
                <PhoneField
                  defaultCountry="za"
                  value={cellNumber.includes(cellDialCode) ? cellNumber : [cellDialCode, cellNumberReplaced].join(' ')}
                  disabled={disableFields}
                  onChange={createPhoneFieldChangeHandler(
                    'cellNumber',
                    setFieldValue,
                    setCellDialCode,
                    hasNumberBeyondDialCode,
                  )}
                />
              }
              label="Phone number"
              error={getFieldError('cellNumber')}
            />
          </FormField>
        </div>
      </div>
    </>
  )
}

export default InviteUserDetails
